import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, PenLine, Eraser, ShieldCheck, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CREDITOR_ID = "DE87ZZZ00002910828";

function SignaturePad({ onChange }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const dirty = useRef(false);

    useEffect(() => {
        const c = canvasRef.current;
        const rect = c.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        c.width = rect.width * dpr;
        c.height = rect.height * dpr;
        const ctx = c.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2.2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#1e293b";
    }, []);

    const pos = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const start = (e) => {
        e.preventDefault();
        drawing.current = true;
        const ctx = canvasRef.current.getContext("2d");
        const p = pos(e);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
    };
    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const p = pos(e);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        if (!dirty.current) { dirty.current = true; onChange(true); }
    };
    const end = () => { drawing.current = false; };

    const clear = () => {
        const c = canvasRef.current;
        c.getContext("2d").clearRect(0, 0, c.width, c.height);
        dirty.current = false;
        onChange(false);
    };

    return (
        <div>
            <div className="relative rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden">
                <canvas
                    ref={canvasRef}
                    data-testid="sepa-signature-canvas"
                    className="w-full h-32 touch-none cursor-crosshair block"
                    onPointerDown={start}
                    onPointerMove={move}
                    onPointerUp={end}
                    onPointerLeave={end}
                />
                <span className="absolute bottom-2 left-3 text-[10px] uppercase tracking-wider text-slate-400 pointer-events-none inline-flex items-center gap-1">
                    <PenLine size={10} /> Hier unterschreiben (Maus oder Finger)
                </span>
            </div>
            <button type="button" onClick={clear} data-testid="sepa-clear-signature" className="mt-1.5 text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1">
                <Eraser size={11} /> Unterschrift löschen
            </button>
        </div>
    );
}

export const getSignatureDataUrl = (container) => {
    const canvas = container.querySelector("canvas");
    return canvas ? canvas.toDataURL("image/png") : "";
};

export default function SepaMandateModal({ open, pkg, onClose, onSigned }) {
    const [mandate, setMandate] = useState(null);
    const [name, setName] = useState("");
    const [consent, setConsent] = useState(false);
    const [hasSig, setHasSig] = useState(false);
    const [busy, setBusy] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        if (!open || !pkg) return;
        setMandate(null);
        setName("");
        setConsent(false);
        setHasSig(false);
        api.post("/payments/sepa-mandate/init", { package_id: pkg.id })
            .then(({ data }) => setMandate(data))
            .catch((e) => {
                toast.error(formatApiError(e.response?.data?.detail) || "Mandat konnte nicht erstellt werden");
                onClose();
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, pkg?.id]);

    if (!open || !pkg) return null;

    const canSubmit = mandate && name.trim().length >= 2 && consent && hasSig && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            const signature = getSignatureDataUrl(boxRef.current);
            await api.post(`/payments/sepa-mandate/${mandate.mandate_id}/sign`, { name: name.trim(), signature });
            toast.success(`SEPA-Mandat ${mandate.mandate_ref} unterschrieben — PDF kommt per E-Mail`);
            onSigned();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Unterschrift fehlgeschlagen");
            setBusy(false);
        }
    };

    const priceStr = typeof pkg.price === "number" ? `${pkg.price.toFixed(2).replace(".", ",")} €/Monat` : "";

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="sepa-mandate-modal">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
                <div className="p-6 pb-4 border-b border-slate-100 flex items-start justify-between sticky top-0 bg-white rounded-t-3xl">
                    <div>
                        <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-red-600">SEPA-Lastschriftmandat</p>
                        <h2 className="font-heading text-xl font-bold text-slate-900 mt-1">
                            {pkg.name}-Abo {priceStr && <span className="text-slate-400 font-normal text-sm">· {priceStr}</span>}
                        </h2>
                    </div>
                    <button onClick={onClose} data-testid="sepa-cancel-btn" className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 shrink-0">
                        <X size={15} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm">
                        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
                            <span className="text-slate-500 text-xs pt-0.5">Gläubiger</span>
                            <span className="font-semibold text-slate-900">Bewerbung24.ai</span>
                            <span className="text-slate-500 text-xs pt-0.5">Gläubiger-ID</span>
                            <span className="font-mono font-semibold text-slate-900" data-testid="sepa-creditor-id">{CREDITOR_ID}</span>
                            <span className="text-slate-500 text-xs pt-0.5">Mandatsreferenz</span>
                            <span className="font-mono font-semibold text-slate-900" data-testid="sepa-mandate-ref">
                                {mandate ? mandate.mandate_ref : <Loader2 size={13} className="animate-spin inline" />}
                            </span>
                            <span className="text-slate-500 text-xs pt-0.5">Zahlungsart</span>
                            <span className="text-slate-700">Wiederkehrende Zahlung (monatlich)</span>
                        </div>
                    </div>

                    <p className="text-xs text-slate-600 leading-relaxed">
                        Ich ermächtige Bewerbung24.ai, Zahlungen von meinem Konto mittels Lastschrift einzuziehen.
                        Zugleich weise ich mein Kreditinstitut an, die von Bewerbung24.ai auf mein Konto gezogenen
                        Lastschriften einzulösen. <b>Hinweis:</b> Ich kann innerhalb von acht Wochen, beginnend mit dem
                        Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit
                        meinem Kreditinstitut vereinbarten Bedingungen.
                    </p>

                    <div>
                        <label className="text-xs font-semibold text-slate-700">Vollständiger Name (Kontoinhaber)</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Max Mustermann"
                            data-testid="sepa-name-input"
                            className="mt-1 w-full px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-red-400 outline-none text-sm"
                        />
                    </div>

                    <div ref={boxRef}>
                        <label className="text-xs font-semibold text-slate-700">Unterschrift</label>
                        <div className="mt-1">
                            <SignaturePad onChange={setHasSig} />
                        </div>
                    </div>

                    <label className="flex items-start gap-2.5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            data-testid="sepa-consent-checkbox"
                            className="mt-0.5 h-4 w-4 accent-red-600"
                        />
                        <span className="text-xs text-slate-700 leading-relaxed">
                            Ich erteile das SEPA-Lastschriftmandat und stimme der elektronischen Unterzeichnung zu.
                        </span>
                    </label>

                    <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-[11px] text-emerald-800 flex items-center gap-2">
                        <Mail size={13} className="shrink-0" />
                        Du erhältst das unterschriebene Mandat sofort als PDF per E-Mail. Die IBAN gibst du im nächsten Schritt sicher bei Stripe ein.
                    </div>

                    <button
                        onClick={submit}
                        disabled={!canSubmit}
                        data-testid="sepa-submit-btn"
                        className="w-full py-3 rounded-full font-semibold text-sm btn-primary-grad inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
                        Unterschreiben &amp; weiter zur Zahlung
                    </button>
                </div>
            </div>
        </div>
    );
}
